export const AI_EFFORT_LEVELS = ["none", "minimal", "low", "medium", "high", "xhigh", "max"] as const;

export type AiEffort = (typeof AI_EFFORT_LEVELS)[number];
export type AiEffortProvider = "anthropic" | "openai";

const OPENAI_GPT_56 = ["none", "low", "medium", "high", "xhigh", "max"] as const;
const OPENAI_GPT_54_55 = ["none", "low", "medium", "high", "xhigh"] as const;
const OPENAI_GPT_54_55_PRO = ["medium", "high", "xhigh"] as const;
const OPENAI_GPT_52 = ["none", "low", "medium", "high", "xhigh"] as const;
const OPENAI_GPT_51 = ["none", "low", "medium", "high"] as const;
const OPENAI_GPT_5 = ["minimal", "low", "medium", "high"] as const;
const OPENAI_GPT_5_PRO = ["high"] as const;
const OPENAI_O_SERIES = ["low", "medium", "high"] as const;
const ANTHROPIC_STANDARD = ["low", "medium", "high", "max"] as const;
const ANTHROPIC_EXTENDED = ["low", "medium", "high", "xhigh", "max"] as const;
const ANTHROPIC_OPUS_45 = ["low", "medium", "high"] as const;

/**
 * Known effort levels before the provider's Models API has been consulted.
 *
 * Anthropic also returns this capability per model; callers should prefer that
 * live metadata when it is available. OpenAI's Models API currently does not
 * expose the levels, so its documented model-family table remains necessary.
 */
export function modelEffortLevels(provider: AiEffortProvider, model: string): AiEffort[] {
  const id = model.trim().toLowerCase();

  if (provider === "anthropic") {
    if (/^claude-(?:opus|sonnet)-5(?:-|$)/.test(id) || /^claude-opus-4-(?:7|8)(?:-|$)/.test(id)) {
      return [...ANTHROPIC_EXTENDED];
    }
    if (/^claude-(?:opus|sonnet)-4-6(?:-|$)/.test(id)) return [...ANTHROPIC_STANDARD];
    if (/^claude-opus-4-5(?:-|$)/.test(id)) return [...ANTHROPIC_OPUS_45];
    return [];
  }

  if (/^gpt-5\.6(?:-|$)/.test(id)) return [...OPENAI_GPT_56];
  if (/^gpt-5\.(?:4|5)-pro(?:-|$)/.test(id)) return [...OPENAI_GPT_54_55_PRO];
  if (/^gpt-5\.(?:4|5)(?:-|$)/.test(id)) return [...OPENAI_GPT_54_55];
  if (/^gpt-5\.2(?:-|$)/.test(id)) return [...OPENAI_GPT_52];
  if (/^gpt-5\.1(?:-|$)/.test(id)) return [...OPENAI_GPT_51];
  if (/^gpt-5-pro(?:-|$)/.test(id)) return [...OPENAI_GPT_5_PRO];
  if (/^gpt-5(?:-|$)/.test(id)) return [...OPENAI_GPT_5];
  if (/^(?:o1|o3|o4-mini)(?:-|$)/.test(id)) return [...OPENAI_O_SERIES];
  return [];
}

export function defaultModelEffort(
  provider: AiEffortProvider,
  model: string,
  levels = modelEffortLevels(provider, model),
): AiEffort | null {
  if (!levels.length) return null;
  const id = model.trim().toLowerCase();
  if (provider === "anthropic") return levels.includes("high") ? "high" : levels[0]!;
  if (/^gpt-5\.4(?!-pro)(?:-|$)/.test(id) && levels.includes("none")) return "none";
  if (/^gpt-5\.4-pro(?:-|$)/.test(id) && levels.includes("medium")) return "medium";
  if (/^gpt-5\.5-pro(?:-|$)/.test(id) && levels.includes("high")) return "high";
  if (/^gpt-5-pro(?:-|$)/.test(id) && levels.includes("high")) return "high";
  return levels.includes("medium") ? "medium" : levels[0]!;
}

export function compatibleModelEffort(
  provider: AiEffortProvider,
  model: string,
  effort: unknown,
  discoveredLevels?: readonly AiEffort[],
): AiEffort | null {
  const levels = discoveredLevels ? [...discoveredLevels] : modelEffortLevels(provider, model);
  return typeof effort === "string" && levels.includes(effort as AiEffort)
    ? effort as AiEffort
    : defaultModelEffort(provider, model, levels);
}

/**
 * Applies a per-step effort ceiling without ever exceeding the effort chosen
 * by the administrator. When a provider skips an intermediate level, use the
 * strongest supported level that is still below both ceilings.
 */
export function cappedModelEffort(
  configured: AiEffort | null,
  requested: AiEffort | undefined,
  supported: readonly AiEffort[],
): AiEffort | null {
  if (!configured || !requested) return configured;
  const configuredRank = AI_EFFORT_LEVELS.indexOf(configured);
  const requestedRank = AI_EFFORT_LEVELS.indexOf(requested);
  const ceiling = Math.min(configuredRank, requestedRank);
  for (let index = ceiling; index >= 0; index -= 1) {
    const candidate = AI_EFFORT_LEVELS[index]!;
    if (supported.includes(candidate)) return candidate;
  }
  return configured;
}
